import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCalculatorStore } from '../../store/calculatorStore';
import { DiscountModifier } from '../shared/DiscountModifier';
import { useBlobStorageCalc, useBandwidthCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { ShareButton } from '../shared/ShareButton';
import { Disclaimer } from '../shared/Disclaimer';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';
import { MobileCostBar } from '../shared/MobileCostBar';
import { FieldTooltip } from '../shared/FieldTooltip';
import { NumberInput } from '../ui/NumberInput';
import { Select } from '../ui/Select';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import { Slider } from '../ui/Slider';
import { Toggle } from '../ui/Toggle';

const TIERS = ['hot', 'cool', 'cold', 'archive'] as const;
const REDUNDANCY_OPTIONS = [
  { value: 'LRS', label: 'LRS', description: 'Locally redundant' },
  { value: 'ZRS', label: 'ZRS', description: 'Zone redundant' },
  { value: 'GRS', label: 'GRS', description: 'Geo-redundant' },
  { value: 'RA-GRS', label: 'RA-GRS', description: 'Read-access geo-redundant' },
] as const;

// D1-verified: SELECT retail_price FROM azure_prices WHERE service_name = 'Storage'
// AND sku_name LIKE '%Archive%' AND arm_region_name = 'uksouth' ORDER BY sku_name;
// Run: npx wrangler d1 execute azure-calc-prices --remote --config workers/api/wrangler.toml
const ARCHIVE_RETRIEVAL_RATE_GBP = 0.0157;

// BlobStorageCalc.tsx
// Converted from Stitch screen: desktop_blob_storage_view/code.html

export default function BlobStorageCalc() {
  const [opsOpen, setOpsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { blobInputs, setBlobInputs, bandwidthInputs, setBandwidthInputs, discount, setDiscount } = useCalculatorStore();
  const { data: calculation, isLoading, isError } = useBlobStorageCalc(blobInputs);
  const { data: bandwidthCalc } = useBandwidthCalc(bandwidthInputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (calculation?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'blob-storage', result_gbp: calculation.monthly_estimate, tier: blobInputs.tier })
    }
  }, [calculation?.monthly_estimate])

  // Convert TB to GB for slider display
  const storageGB = blobInputs.storageTB * 1024;

  const handleSliderChange = (gb: number) => {
    setBlobInputs({ storageTB: gb / 1024 });
  };

  const handleTierChange = (tier: (typeof TIERS)[number]) => {
    setBlobInputs({ tier });
  };

  const handleRedundancyChange = (redundancy: 'LRS' | 'ZRS' | 'GRS' | 'RA-GRS') => {
    setBlobInputs({ redundancy });
  };

  const togglePITR = () => {
    setBlobInputs({ enablePITR: !blobInputs.enablePITR });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0">
      {/* Left Column: Config */}
      <div className="flex flex-col gap-6">
        <div className="border-b border-outline-variant/30 pb-2">
          <h2 className="text-primary font-semibold text-sm">Blob Storage</h2>
          <p className="text-[11px] text-outline mt-1">UK South pricing from the Azure Retail Prices API (estimate).</p>
        </div>

        <div className={`bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>database</span>
              Storage Configuration
            </h2>
          </div>

          <ScenarioPresets presets={[
            { label: 'Small team', description: '~100 GB hot storage, LRS', apply: () => setBlobInputs({ storageTB: 0.1, tier: 'hot', redundancy: 'LRS' }) },
            { label: 'Media archive', description: '50 TB cool storage, LRS', apply: () => setBlobInputs({ storageTB: 50, tier: 'cool', redundancy: 'LRS' }) },
            { label: 'Compliance cold', description: '10 TB cold storage, GRS', apply: () => setBlobInputs({ storageTB: 10, tier: 'cold', redundancy: 'GRS' }) },
          ]} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {/* Region */}
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Region</label>
              <div className="bg-surface-container-lowest border border-outline-variant p-3 flex justify-between items-center cursor-not-allowed opacity-70">
                <span className="text-sm">UK South</span>
                <span className="material-symbols-outlined text-outline">expand_more</span>
              </div>
            </div>

            {/* Redundancy */}
            <div className="flex flex-col gap-2">
              <Select
                label="Redundancy"
                value={blobInputs.redundancy}
                onChange={(value) => handleRedundancyChange(value as 'LRS' | 'ZRS' | 'GRS' | 'RA-GRS')}
                options={[...REDUNDANCY_OPTIONS]}
              />
            </div>

            {/* Storage Slider */}
            <div className="flex flex-col gap-4 md:col-span-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Data Capacity</label>
                <span className="text-xl font-bold font-mono text-primary">
                  {blobInputs.storageTB >= 1 ? `${blobInputs.storageTB.toFixed(1)} TB` : `${Math.round(storageGB)} GB`}
                </span>
              </div>
              <Slider
                value={storageGB}
                onChange={handleSliderChange}
                min={32}
                max={5120}
                step={32}
                label="Data Capacity"
                formatValue={(n) => n >= 1024 ? `${(n / 1024).toFixed(1)} TB` : `${Math.round(n)} GB`}
              />
            </div>

            {/* Access Tier */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Access Tier <FieldTooltip content="Hot = instant access, highest cost. Cool/Cold = cheaper storage, access fees apply. Archive = lowest cost but hours to retrieve." /></label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TIERS.map((t) => (
                  <button 
                    key={t}
                    onClick={() => handleTierChange(t)}
                    type="button"
                    className={`p-3 flex items-center gap-2 transition-all ${blobInputs.tier === t ? 'bg-primary-container/10 border border-primary-container' : 'bg-surface-container-lowest border border-outline-variant hover:bg-surface-container'}`}
                  >
                    <div className={`w-3 h-3 rounded-full shrink-0 ${blobInputs.tier === t ? 'bg-primary shadow-[0_0_8px_rgba(0,120,212,0.6)]' : 'border border-outline'}`} />
                    <span className={`text-sm capitalize ${blobInputs.tier === t ? 'font-bold text-on-surface' : 'font-medium text-outline'}`}>{t}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Archive retrieval warning — only shown when Archive tier is selected */}
          {blobInputs.tier === 'archive' && (
            <div className="mt-4 p-4 bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-sm flex items-start gap-3">
              <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
              <div className="flex flex-col gap-1">
                <p className="text-[12px] font-semibold text-[#e8b000]">Archive retrieval is expensive</p>
                <p className="text-[11px] text-on-surface-variant leading-relaxed">
                  Reading data from Archive costs <span className="font-bold font-mono">£{ARCHIVE_RETRIEVAL_RATE_GBP.toFixed(3)}/GB</span> in retrieval fees,
                  plus rehydration takes <span className="font-bold">up to 15 hours</span> before data is accessible.
                  Archive is only cost-effective if data is <span className="font-bold">never or very rarely retrieved</span>.
                  If you need occasional access, Cool or Cold tier is almost always cheaper overall.
                </p>
              </div>
            </div>
          )}

          {/* Smart Tier callout */}
          <div className="mt-4 p-3 bg-surface-container-lowest/60 border border-outline-variant/50 rounded-sm flex items-start gap-2">
            <span className="material-symbols-outlined text-outline text-[16px] shrink-0 mt-0.5">info</span>
            <p className="text-[11px] text-outline leading-snug italic">
              Smart Tier (preview): automatically moves blobs between Hot, Cool, and Cold based on access patterns. Pricing follows destination tier rates. Not yet modelled in this calculator.
            </p>
          </div>

          {/* Operations (expanded only when needed) */}
          <div className="mt-6 border-t border-outline-variant/30 pt-4">
            <button
              type="button"
              onClick={() => setOpsOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-outline">settings</span>
                <span className="font-headline font-bold text-sm">Operations &amp; Data Transfer</span>
              </div>
              <span className="material-symbols-outlined text-outline">{opsOpen ? 'remove' : 'add'}</span>
            </button>

            {opsOpen && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <NumberInput
                    label="Write operations / month"
                    value={blobInputs.writeOpsPerMonth ?? 0}
                    onChange={(value) => setBlobInputs({ writeOpsPerMonth: value })}
                    min={0}
                    step={1000}
                    inputMode="numeric"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <NumberInput
                    label="Read operations / month"
                    value={blobInputs.readOpsPerMonth ?? 0}
                    onChange={(value) => setBlobInputs({ readOpsPerMonth: value })}
                    min={0}
                    step={1000}
                    inputMode="numeric"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <NumberInput
                    label="List operations / month"
                    value={blobInputs.listOpsPerMonth ?? 0}
                    onChange={(value) => setBlobInputs({ listOpsPerMonth: value })}
                    min={0}
                    step={1000}
                    inputMode="numeric"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <NumberInput
                    label="GB retrieved / month"
                    value={blobInputs.gbRetrievedPerMonth ?? 0}
                    onChange={(value) => setBlobInputs({ gbRetrievedPerMonth: value })}
                    min={0}
                    step={10}
                    inputMode="numeric"
                  />
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <NumberInput
                    label="Days stored (for early deletion penalties)"
                    value={blobInputs.daysStored ?? 31}
                    onChange={(value) => setBlobInputs({ daysStored: value })}
                    min={0}
                    step={1}
                    inputMode="numeric"
                  />
                </div>
              </div>
            )}
          </div>

          {/* PITR */}
          <div className="mt-4 border-t border-outline-variant/30 pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-outline">history</span>
                <span className="font-headline font-bold text-sm">Point-in-time Restore (PITR)</span>
              </div>
              <Toggle
                checked={!!blobInputs.enablePITR}
                onChange={() => togglePITR()}
                label="Enable PITR"
              />
            </div>

            {blobInputs.enablePITR && (
              <div className="flex flex-col gap-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <NumberInput
                    label="GB to restore (per incident)"
                    value={blobInputs.gbToRestoreIfPITR ?? 0}
                    onChange={(value) => setBlobInputs({ gbToRestoreIfPITR: value })}
                    min={0}
                    step={1}
                    inputMode="numeric"
                  />
                  <NumberInput
                    label="Change feed ops / month"
                    value={blobInputs.changeOpsPerMonth ?? 0}
                    onChange={(value) => setBlobInputs({ changeOpsPerMonth: value })}
                    min={0}
                    step={1000}
                    inputMode="numeric"
                  />
                </div>
                <p className="text-[10px] text-outline italic leading-relaxed">
                  PITR typically requires blob versioning + soft delete + change feed enabled. Costs shown here are estimates and may vary by account settings.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-[#bc5b00]" style={{ fontVariationSettings: "'FILL' 1" }}>output_circle</span>
              Bandwidth / Egress
            </h2>
            <span className="text-[10px] font-bold text-[#107c10] bg-[#107c10]/10 px-2 py-0.5 border border-[#107c10]/30 flex items-center gap-1 uppercase">
              <span className="material-symbols-outlined text-[12px]">check_circle</span>
              FIRST 100GB FREE
            </span>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Outbound internet transfer</label>
                <span className="text-xl font-bold font-mono text-on-surface">{(bandwidthInputs.outboundInternetGB ?? 0).toLocaleString()} GB</span>
              </div>
              <Slider
                value={bandwidthInputs.outboundInternetGB ?? 0}
                onChange={(value) => setBandwidthInputs({ outboundInternetGB: value })}
                min={0}
                max={5000}
                step={1}
                label="Outbound internet transfer"
                formatValue={(n) => `${n.toLocaleString()} GB`}
              />
              <p className="text-[10px] text-outline italic">Pricing based on Routing Preference: Microsoft Premium Network</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Inter-region transfer (Europe)</label>
                <span className="text-xl font-bold font-mono text-on-surface">{(bandwidthInputs.interRegionGB ?? 0).toLocaleString()} GB</span>
              </div>
              <Slider
                value={bandwidthInputs.interRegionGB ?? 0}
                onChange={(value) => setBandwidthInputs({ interRegionGB: value })}
                min={0}
                max={5000}
                step={1}
                label="Inter-region transfer (Europe)"
                formatValue={(n) => `${n.toLocaleString()} GB`}
              />
            </div>
            <div className="flex items-start gap-3 bg-surface-container-lowest/40 border border-outline-variant/40 p-3 rounded-sm">
              <span className="material-symbols-outlined text-outline text-base">info</span>
              <p className="text-[10px] text-outline leading-relaxed">
                This egress estimate is shown alongside Blob Storage for convenience. For full detail and tiering behaviour, use the{' '}
                <Link to="/bandwidth" className="text-primary font-semibold hover:underline">
                  Bandwidth calculator
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Results */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {/* Results Card */}
          <div id="blob-results" className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden flex flex-col">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${calculation?.monthly_estimate.toFixed(2)}`}
                </span>
                {!isLoading && !isError && calculation && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {calculation?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">{item.label}</span>
                    <span className="font-bold">£{item.value.toFixed(2)}</span>
                  </li>
                ))}
                {bandwidthCalc && ((bandwidthInputs.outboundInternetGB ?? 0) > 0 || (bandwidthInputs.interRegionGB ?? 0) > 0) && (
                  <li className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">Egress (separate estimate)</span>
                    <span className="font-bold">£{bandwidthCalc.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
                {!calculation && !isLoading && (
                  <p className="text-[10px] text-outline text-center py-4">Adjust configuration to see pricing</p>
                )}
                {calculation && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{calculation.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>
            {calculation && (
              <div className="px-6 pb-4">
                <FormulaDisclosure
                  lines={[
                    ...calculation.breakdown.map((item) => `${item.label}: £${item.value.toFixed(4)}`),
                    `Tier: ${blobInputs.tier.charAt(0).toUpperCase() + blobInputs.tier.slice(1)} · Redundancy: ${blobInputs.redundancy} · Region: UK South`,
                    ...(blobInputs.tier === 'archive'
                      ? [`Note: retrieval costs £${ARCHIVE_RETRIEVAL_RATE_GBP.toFixed(3)}/GB if data is read (not included in estimate above)`]
                      : []),
                  ]}
                  fetchedAt={calculation.prices_last_updated}
                />
              </div>
            )}
            <div className="p-4 bg-surface-container-highest">
              <ShareButton inputs={blobInputs} result={calculation ?? null} service="blob-storage" />
            </div>
          </div>

          {/* Tier Comparison */}
          {calculation?.tier_comparison && (
            <div className="bg-surface-container-high ghost-border rounded-lg p-5">
              <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-4">Tier Comparison ({blobInputs.storageTB >= 1 ? `${blobInputs.storageTB.toFixed(1)} TB` : `${Math.round(storageGB)} GB`})</h4>
              <div className="space-y-2">
                {calculation.tier_comparison.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`flex justify-between items-center p-2 cursor-pointer transition-all ${item.isCurrentSelection ? 'bg-primary-container/20 border-l-2 border-primary' : 'hover:bg-surface-container'}`}
                    onClick={() => handleTierChange(item.tier.toLowerCase() as (typeof TIERS)[number])}
                  >
                    <span className={`text-xs ${item.isCurrentSelection ? 'font-bold' : 'text-on-surface-variant'}`}>{item.tier} {item.isCurrentSelection && '(Current)'}</span>
                    <div className="flex flex-col items-end">
                      <span className="text-xs font-mono">£{item.total.toFixed(2)}</span>
                      {item.isCheapest && !item.isCurrentSelection && <span className="text-[9px] text-primary font-bold uppercase">Best Value</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {calculation && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={calculation.monthly_estimate}
            />
          )}
          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
          </div>
          <ResourcesSection service="blob-storage" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="blob-storage"
          estimateValue={calculation ? `£${calculation.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={calculation?.monthly_estimate}
        label={`Blob Storage (${blobInputs.tier.charAt(0).toUpperCase() + blobInputs.tier.slice(1)}, ${blobInputs.redundancy})`}
        targetId="blob-results"
      />
    </div>
  );
}
